<template>
    <v-container>
        <v-row justify="center" class="mt-5">
            <v-col cols="12" sm="9">
                <v-card>
                    <v-card-title>{{ $t('set_new_pw_title') }}</v-card-title>

                    <!-- while the token is being checked -->
                    <v-card-text v-if="checking">
                        <v-skeleton-loader type="article" />
                    </v-card-text>

                    <!-- expired, already used or wrong token -->
                    <template v-else-if="!tokenValid">
                        <v-card-text>
                            <v-alert type="error">{{ $t('set_new_pw_invalid') }}</v-alert>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer />
                            <ActionBtn color="action white--text" :to="{ name: 'reset_pw' }">
                                {{ $t('forgot_pw') }}
                            </ActionBtn>
                        </v-card-actions>
                    </template>

                    <!-- the actual form -->
                    <v-form v-else ref="form" v-model="formValid" @submit.prevent="send">
                        <v-card-text>
                            <v-alert type="info" color="primary">
                                {{ $t('set_new_pw_text', { username: username }) }}
                            </v-alert>
                            <LabelField :label="$t('new_pw')">
                                <v-text-field
                                    v-model="password"
                                    type="password"
                                    dense
                                    outlined
                                    autocomplete="new-password"
                                    :rules="passwordRules"
                                    :autofocus="true"
                                    @input="$refs.form.validate()"
                                />
                            </LabelField>
                            <LabelField :label="$t('confirm_pw')">
                                <v-text-field
                                    v-model="passwordConfirm"
                                    type="password"
                                    dense
                                    outlined
                                    autocomplete="new-password"
                                    :rules="passwordConfirmRules"
                                    @input="$refs.form.validate()"
                                />
                            </LabelField>
                            <p>{{ $t('set_new_pw_totp_hint') }}</p>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer />
                            <PlainBtn :to="{ name: 'login' }" :disabled="loading">{{ $t('cancel') }}</PlainBtn>
                            <ActionBtn
                                color="action white--text"
                                :loading="loading"
                                :disabled="loading || !formValid"
                                type="submit"
                                @click.prevent="send"
                            >
                                {{ $t('absenden') }}
                            </ActionBtn>
                        </v-card-actions>

                        <v-fade-transition>
                            <v-card-text v-if="error">
                                <v-alert type="error">{{ error }}</v-alert>
                            </v-card-text>
                        </v-fade-transition>
                    </v-form>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { checkResetToken, setNewPassword } from '@/helpers/auth';
import { formRules, errToStr } from '@/helpers/tools';

const PW_MIN_LENGTH = 8;

/**
 * "Neues Passwort setzen": the mask the "Passwort vergessen" mail links to.
 *
 * The token in the query identifies the user - the mask asks for no username. Redeeming it
 * also clears the TOTP setup on the server, so the next login runs the onboarding.
 */
export default {
    name: 'SetNewPassword',
    data() {
        return {
            token: '',
            username: '',
            checking: true,
            tokenValid: false,
            password: '',
            passwordConfirm: '',
            formValid: false,
            loading: false,
            error: null,
        };
    },
    computed: {
        passwordRules() {
            return [formRules.minLength(PW_MIN_LENGTH)];
        },
        passwordConfirmRules() {
            return [
                formRules.minLength(PW_MIN_LENGTH),
                () => this.password === this.passwordConfirm || this.$t('pw_not_match'),
            ];
        },
    },
    async created() {
        this.token = String(this.$route.query.token || '');
        try {
            const res = await checkResetToken(this.token);
            this.tokenValid = res.valid === true;
            this.username = res.username || '';
        } catch (e) {
            this.tokenValid = false;
        } finally {
            this.checking = false;
        }
    },
    methods: {
        async send() {
            if (this.loading || !this.formValid) {
                return;
            }
            this.loading = true;
            this.error = null;
            try {
                const res = await setNewPassword(this.token, this.password);
                if (res.changed) {
                    this.$toast({ message: this.$t('set_new_pw_success'), color: 'success', timeout: 8000 });
                    this.$router.replace({ name: 'login' });
                    return;
                }
                // the token expired while the user was typing, or the password is too short
                if (res.invalid_token) {
                    this.tokenValid = false;
                    return;
                }
                this.error = this.$t('pw_too_short', { count: res.min_length || PW_MIN_LENGTH });
            } catch (e) {
                this.error = errToStr(e);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
